import React, { useEffect, useRef, useState } from "react";
import { Table, TableColumnsType, Spin, Menu, Dropdown } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../store";
import { getProductComponentsAPI } from "../productApis";
import { getVariableLabel } from "../../StockProcessManager/constants";
import { DeleteStockComponentAPI } from "../../../Redux/stockProcessManager";
import { getDecryptedName } from "../../../../helpers/bycrypt";
import { formatToLocalDate, useDebounce } from "../../../../helpers/utils";
import NoDataMessage from "../../../Components/NoData";
import { svg } from "../../../../Assets/svg";
import InfiniteScroll from "react-infinite-scroll-component";
import CommonDeleteModal from "../../../Components/Modals/CommonDeleteModal";

interface ProductComponentsProps {
  productId?: string;
}

const ProductComponents: React.FC<ProductComponentsProps> = ({ productId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [componentsList, setComponentsList] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [isDeleteId, setIsDeleteId] = useState<any>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const searchBarHandler = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);
  const searchBarClean = () => setSearch("");

  const fetchComponents = async () => {
    if (!productId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    const filters = {
      product_id: productId,
      search: debouncedSearch,
    };

    dispatch(
      getProductComponentsAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          setComponentsList(data?.components_data || []);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (productId) {
      fetchComponents();
    }
  }, [productId, debouncedSearch]);

  const handleDeleteComponentData = () => {
    const payload = { id: isDeleteId };
    setDeleteLoading(true);
    const onDeleteSuceess = () => {
      setDeleteLoading(false);
      setIsDeleteModal(false);
      setIsDeleteId(null);
      fetchComponents();
    };
    dispatch(DeleteStockComponentAPI(payload, onDeleteSuceess));
  };

  const MoreOptionsMenu = (record: any) => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            navigate(`/process-manager/component/create/${record?._id}`, {
              state: { returnUrl: location.pathname + location.search },
            });
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Modify
          </div>
        </Menu.Item>
        <Menu.Item
          key="2"
          onClick={() => {
            navigate(`/process-manager/component/duplicate/${record?._id}`, {
              state: { returnUrl: location.pathname + location.search },
            });
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Duplicate
          </div>
        </Menu.Item>
        <Menu.Item
          key="3"
          onClick={() => {
            setIsDeleteId(record?._id);
            setIsDeleteModal(true);
          }}
        >
          <div className="text-danger d-flex align-items-center gap-2 svg-20">
            Remove
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Component Name",
      dataIndex: "name",
      key: "name",
      render: (name: any) => name || "-",
    },
    {
      title: "Stock Type",
      dataIndex: "stock_id",
      key: "stock_id",
      render: (stock: any) =>
        stock?.name || stock?.stock_type_name || stock?.title || "-",
    },
    {
      title: "Processes",
      dataIndex: "process_ids",
      key: "process_ids",
      render: (process_ids: any[]) =>
        process_ids?.length ? `${process_ids.length} Processes` : "0",
    },
    {
      title: "Variables",
      dataIndex: "variables",
      key: "variables",
      render: (variables: any[]) => {
        const requiredVars = variables
          ?.filter((v: any) => v.is_required)
          .map((v: any) =>
            getVariableLabel ? getVariableLabel(v.key) : v.key,
          );
        return requiredVars?.length ? requiredVars.join(", ") : "-";
      },
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (cb: any, record: any) => {
        const user = cb || record?.created_by;
        const fullName = getDecryptedName(user) || "-";
        return <span>{fullName}</span>;
      },
    },
    {
      title: "Updated On",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (date: string) => formatToLocalDate(date, true) || "-",
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (action: any, record: any) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center flex-shrink-0">
        <h3 className="fw-bold mb-0 d-flex gap-2 align-items-center">
          Components
        </h3>
        <div
          className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-sm-2 order-3`}
        >
          <div
            className={`pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2`}
          >
            <input
              type="text"
              value={search}
              className="form-control form-control-lg"
              onChange={searchBarHandler}
              placeholder="Search"
            />
            {search?.length ? (
              <span className="cursor-pointer" onClick={searchBarClean}>
                {svg?.searchClear}
              </span>
            ) : (
              <span>{svg?.search}</span>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-y-auto h-100 p-0" id="componentsScrollableDiv">
        {loading ? (
          <div className="card page-table-card variantsTableCard border-0 shadow rounded-4 text-center p-5">
            <Spin size="large" />
          </div>
        ) : componentsList.length === 0 ? (
          <div className="card page-table-card variantsTableCard border-0 shadow rounded-4 p-4">
            <NoDataMessage
              icon={svg?.noProductsIcon}
              text="No components found for this product."
            />
          </div>
        ) : (
          <div style={{ height: "100%" }}>
            {componentsList.map((partGroup: any) => (
              <div key={partGroup.part_id} className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h4 className="fw-bold mb-0 fs-5 text-black">
                    {partGroup.part_name}{" "}
                    {partGroup.components_count > 0 && (
                      <span>({partGroup.components_count})</span>
                    )}
                  </h4>
                </div>
                <div className="card page-table-card variantsTableCard border-0 shadow rounded-4">
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <Table
                        dataSource={partGroup.components}
                        pagination={false}
                        columns={columns}
                        rowKey={(record) => record._id}
                        className="variant-edit-table bg-white"
                        bordered
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isDeleteModal && (
        <CommonDeleteModal
          visible={isDeleteModal}
          setIsModalVisible={setIsDeleteModal}
          handleDelete={handleDeleteComponentData}
          title={"Remove Component, Are You Sure?"}
          description={
            "Are you sure you want to remove this component? All associated data will be permanently deleted."
          }
          setIsDeleteId={setIsDeleteId}
          isLoading={deleteLoading}
          buttonName="REMOVE"
        />
      )}
    </>
  );
};

export default ProductComponents;
